import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppDispatch, useAppSelector } from '../../store';
import UserSlice from '../../store/slices/User';

// PACKAGES
import { Link, useLocation } from 'react-router-dom';
import { Alert } from 'reactstrap';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import ScrollToTopButton from '../../components/ScrollToTopButton';
import StatusIcon from '../../components/StatusIcon';
import Button from '../../components/Button';
import DisplayDate from '../../components/DisplayDate';
import LoadingSpinner from '../../components/LoadingSpinner';

// ICONS
import { ReactComponent as PlusIcon } from '../../assets/svg/plus.svg';
import { ReactComponent as SwitchUserIcon } from '../../assets/svg/switch-user.svg';
import { ReactComponent as SwitchIcon } from '../../assets/svg/switch.svg';

// TYPES
import { FeedbackAdminDashboardFeedback, FeedbackUserDashboardFeedback, DomainFeedbackStatus } from '../../api/models';

const Feedback: React.FC = () => {
	const dispatch = useAppDispatch();
	const location = useLocation();

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Check if Organisation is active
	const isOrganisationActive = useAppSelector((state) => state.user.isOrganisationActive);

	// Check if User is Organisation Admin
	const isOrganisationAdmin = useAppSelector((state) => state.user.isOrganisationAdmin);

	// Check if User is viewing Feedback as an Admin or not
	const viewFeedbackAsAdmin = useAppSelector((state) => state.user.viewFeedbackAsAdmin);

	// Set up Admin Data state and initial Admin data
	const [adminFeedbackData, setAdminFeedbackData] = React.useState<FeedbackAdminDashboardFeedback[]>([]);

	const getAdminFeedbackData = async () => {
		setIsLoading(true);

		const response = await apibridge.getAdminFeedbackData();

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = [] } = response;

			setIsLoading(false);

			setAdminFeedbackData(result);
		}
	};

	// Set up User Data state and initial User data
	const [userFeedbackData, setUserFeedbackData] = React.useState<FeedbackUserDashboardFeedback[]>([]);

	const getUserFeedbackData = async () => {
		setIsLoading(true);

		const response = await apibridge.getUserFeedbackData();

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = [] } = response;

			setIsLoading(false);

			setUserFeedbackData(result);
		}
	};

	// Check if User follow the user-view Link from Dashboard
	React.useEffect(() => {
		if (location.pathname === '/feedback/user-view') {
			dispatch(UserSlice.actions.update({ viewFeedbackAsAdmin: false }));

			getUserFeedbackData();
		} else if (isOrganisationAdmin && location.pathname === '/feedback/admin-view') {
			dispatch(UserSlice.actions.update({ viewFeedbackAsAdmin: true }));

			getAdminFeedbackData();
		}
	}, []);

	// Load data based on which view is loaded
	React.useEffect(() => {
		if (isOrganisationAdmin && viewFeedbackAsAdmin) {
			getAdminFeedbackData();
		} else {
			getUserFeedbackData();
		}

		setIsLoading(false);
	}, []);

	const defaultMaxItems: number = 5;

	// FILTER ADMIN DATA
	const submittedAdminFeedbackData = adminFeedbackData.filter(
		(item) => item?.status === DomainFeedbackStatus.Submitted
	);
	const inProgressAdminFeedbackData = adminFeedbackData.filter(
		(item) => item?.status === DomainFeedbackStatus.InProgress
	);
	const resolvedAdminFeedbackData = adminFeedbackData.filter(
		(item) => item?.status === DomainFeedbackStatus.Resolved || item?.status === DomainFeedbackStatus.Withdrawn
	);

	// State to manage maximum number of Admin items per section
	const [maxAdminSubmittedFeedback, setMaxAdminSubmittedFeedback] = React.useState<number>(defaultMaxItems);
	const [maxAdminInProgressFeedback, setMaxAdminInProgressFeedback] = React.useState<number>(defaultMaxItems);
	const [maxAdminResolvedFeedback, setMaxAdminResolvedFeedback] = React.useState<number>(defaultMaxItems);

	// FILTER USER DATA
	const submittedUserFeedbackData = userFeedbackData.filter((item) => item?.status === DomainFeedbackStatus.Submitted);
	const inProgressUserFeedbackData = userFeedbackData.filter(
		(item) => item?.status === DomainFeedbackStatus.InProgress
	);
	const resolvedUserFeedbackData = userFeedbackData.filter(
		(item) => item?.status === DomainFeedbackStatus.Resolved || item?.status === DomainFeedbackStatus.Withdrawn
	);

	// State to manage maximum number of User items per section
	const [maxUserSubmittedFeedback, setMaxUserSubmittedFeedback] = React.useState<number>(defaultMaxItems);
	const [maxUserInProgressFeedback, setMaxUserInProgressFeedback] = React.useState<number>(defaultMaxItems);
	const [maxUserResolvedFeedback, setMaxUserResolvedFeedback] = React.useState<number>(defaultMaxItems);

	// Switch Role Function
	const switchRole = () => {
		if (isOrganisationAdmin) {
			if (viewFeedbackAsAdmin) {
				dispatch(UserSlice.actions.update({ viewFeedbackAsAdmin: false }));

				getUserFeedbackData();
			} else {
				dispatch(UserSlice.actions.update({ viewFeedbackAsAdmin: true }));

				getAdminFeedbackData();
			}
		}
	};

	return (
		<div className="page-general-layout feedback-page">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								{isOrganisationAdmin && viewFeedbackAsAdmin ? (
									<>
										<p className="sub-grey-text mbtom-30">
											Feedback is a vital way for organisations to improve child safety. As a Leader, you can view and
											manage feedback received by your organisation.
										</p>

										<Alert color="info">
											<div className="alert-icon">
												<SwitchUserIcon />
											</div>

											<div className="alert-content">
												<p>
													Below is the feedback that your organisation has received. As a <strong>Leader</strong>, you
													can view and manage this feedback.
												</p>

												<p>
													You can also submit and view <strong>your own feedback</strong>. Click on ‘View my feedback’
													to switch roles.
												</p>
											</div>

											<Button className="btn-primary small" onClick={() => switchRole()}>
												<SwitchIcon className="link-icon" /> View my feedback
											</Button>
										</Alert>

										{adminFeedbackData && adminFeedbackData.length > 0 ? (
											<>
												{isOrganisationActive && (
													<div className="submit-btn-container">
														<Link to="/feedback-submit" className="btn-primary submit-btn">
															Submit feedback
														</Link>
													</div>
												)}

												{submittedAdminFeedbackData && submittedAdminFeedbackData.length > 0 && (
													<section className="info-section complaints-page">
														<div className="section-header bold">
															New
															<span className="quantity">{submittedAdminFeedbackData.length}</span>
														</div>

														{submittedAdminFeedbackData.slice(0, maxAdminSubmittedFeedback).map((item) => {
															return (
																<div key={item.id} className="row-container">
																	<div className="row detail-row feedback">
																		<div className="col-auto pdl-0 pdr-0">
																			<StatusIcon statusType="danger" />
																		</div>

																		<div className="col-8 col-lg-auto full-width">
																			<div className="row detail-info">
																				<div className="col-lg-3 detail-name">{item.subject}</div>

																				<div className="col-lg-3 detail-status">
																					<div className="title red">Submitted</div>

																					<time className="name red">
																						<DisplayDate>{item.created}</DisplayDate>
																					</time>
																				</div>

																				<div className="col-lg-3 detail-status">
																					<div className="title">By</div>

																					<div className="status">{item.creator}</div>
																				</div>

																				<div className="col-lg-3 detail-status">
																					<div className="title">Days open</div>

																					<time className="status">{item.daysOpen}</time>
																				</div>
																			</div>
																		</div>

																		<div className="col-auto pdr-0 link-group">
																			<Link className="btn-text complaints-page" to={`/feedback/${item.id}`}>
																				View
																			</Link>
																		</div>
																	</div>
																</div>
															);
														})}

														{maxAdminSubmittedFeedback < submittedAdminFeedbackData.length && (
															<Button
																className="btn-secondary btn-show-more"
																onClick={() => setMaxAdminSubmittedFeedback(submittedAdminFeedbackData.length)}
																buttonIcon={<PlusIcon className="link-icon" />}
															>
																Show {submittedAdminFeedbackData.length - maxAdminSubmittedFeedback} more
															</Button>
														)}
													</section>
												)}

												{inProgressAdminFeedbackData && inProgressAdminFeedbackData.length > 0 && (
													<section className="info-section complaints-page">
														<div className="section-header bold">In Progress</div>
														{inProgressAdminFeedbackData.slice(0, maxAdminInProgressFeedback).map((item) => {
															return (
																<div key={item.id} className="row-container">
																	<div className="row detail-row">
																		<div className="col-auto pdl-0 pdr-0">
																			<StatusIcon statusType="warning" />
																		</div>

																		<div className="col-md-auto full-width">
																			<div className="row detail-info">
																				<div className="col-lg-3 detail-name">{item.subject}</div>

																				<div className="col-lg-3 detail-status">
																					<div className="title">Last updated</div>

																					<time className="name">
																						<DisplayDate>{item.updated}</DisplayDate>
																					</time>
																				</div>

																				<div className="col-lg-3 detail-status">
																					<div className="title">By</div>

																					<div className="status">{item.creator}</div>
																				</div>

																				<div className="col-lg-3 detail-status">
																					<div className="title">Days open</div>

																					<time className="status">{item.daysOpen}</time>
																				</div>
																			</div>
																		</div>

																		<div className="col-auto pdr-0 link-group">
																			<Link className="btn-text complaints-page" to={`/feedback/${item.id}`}>
																				View
																			</Link>
																		</div>
																	</div>
																</div>
															);
														})}

														{maxAdminInProgressFeedback < inProgressAdminFeedbackData.length && (
															<Button
																className="btn-secondary btn-show-more"
																onClick={() => setMaxAdminInProgressFeedback(inProgressAdminFeedbackData.length)}
																buttonIcon={<PlusIcon className="link-icon" />}
															>
																Show {inProgressAdminFeedbackData.length - maxAdminInProgressFeedback} more
															</Button>
														)}
													</section>
												)}

												{resolvedAdminFeedbackData && resolvedAdminFeedbackData.length > 0 && (
													<section className="info-section complaints-page">
														<div className="section-header bold">Resolved</div>
														{resolvedAdminFeedbackData.slice(0, maxAdminResolvedFeedback).map((item) => {
															return (
																<div key={item.id} className="row-container">
																	<div className="row detail-row">
																		<div className="col-auto pdl-0 pdr-0">
																			<StatusIcon statusType="success" />
																		</div>

																		<div className="col-md-auto full-width">
																			<div className="row detail-info">
																				<div className="col-lg-3 detail-name">{item.subject}</div>

																				<div className="col-lg-3 detail-status">
																					{item.status === DomainFeedbackStatus.Resolved ? (
																						<div className="title resolved">Resolved</div>
																					) : null}

																					{item.status === DomainFeedbackStatus.Withdrawn ? (
																						<div className="title withdrawn">Withdrawn</div>
																					) : null}

																					<time className="name">
																						<DisplayDate>{item.resolved}</DisplayDate>
																					</time>
																				</div>

																				<div className="col-lg-3 detail-status">
																					<div className="title">By</div>

																					<div className="status">{item.creator}</div>
																				</div>

																				<div className="col-lg-3 detail-status">
																					<div className="title">Days open</div>

																					<time className="status">{item.daysOpen}</time>
																				</div>
																			</div>
																		</div>

																		<div className="col-auto pdr-0 link-group">
																			<Link className="btn-text complaints-page" to={`/feedback/${item.id}`}>
																				View
																			</Link>
																		</div>
																	</div>
																</div>
															);
														})}

														{maxAdminResolvedFeedback < resolvedAdminFeedbackData.length && (
															<Button
																className="btn-secondary btn-show-more"
																onClick={() => setMaxAdminResolvedFeedback(resolvedAdminFeedbackData.length)}
																buttonIcon={<PlusIcon className="link-icon" />}
															>
																Show {resolvedAdminFeedbackData.length - maxAdminResolvedFeedback} more
															</Button>
														)}
													</section>
												)}

												<ScrollToTopButton offsetValue={100} />
											</>
										) : (
											<div className="empty-notification">
												<h2 className="info-text">
													You have not yet submitted any <strong>Feedback</strong>.
												</h2>

												{isOrganisationActive && (
													<Link to="/feedback-submit" className="btn-primary submit-btn">
														Submit feedback
													</Link>
												)}
											</div>
										)}
									</>
								) : (
									<>
										<p className="sub-grey-text mbtom-30">
											Feedback is a vital way for organisations to improve child safety. As a member of your
											organisation, you can submit feedback and view your own feedback.
										</p>

										{isOrganisationAdmin && (
											<Alert color="info">
												<div className="alert-icon">
													<SwitchUserIcon />
												</div>

												<div className="alert-content">
													<p>
														Below is the feedback that <strong>you have submitted</strong> to your organisation.
													</p>

													<p>
														As a <strong>Leader</strong>, you can also view and manage feedback received by your
														organisation. Click on ‘Manage feedback’ to switch roles.
													</p>
												</div>

												<Button className="btn-primary small" onClick={() => switchRole()}>
													<SwitchIcon className="link-icon" />
													Manage feedback
												</Button>
											</Alert>
										)}

										{userFeedbackData && userFeedbackData.length > 0 ? (
											<>
												{isOrganisationActive && (
													<div className="submit-btn-container">
														<Link to="/feedback-submit" className="btn-primary submit-btn">
															Submit feedback
														</Link>
													</div>
												)}

												{submittedUserFeedbackData && submittedUserFeedbackData.length > 0 && (
													<section className="info-section complaints-page">
														<div className="section-header bold">
															New
															<span className="quantity">{submittedUserFeedbackData.length}</span>
														</div>

														{submittedUserFeedbackData.slice(0, maxUserSubmittedFeedback).map((item) => {
															return (
																<div key={item.id} className="row-container">
																	<div className="row detail-row feedback">
																		<div className="col-auto pdl-0 pdr-0">
																			<StatusIcon statusType="danger" />
																		</div>

																		<div className="col-8 col-lg-auto full-width">
																			<div className="row detail-info">
																				<div className="col-lg-3 detail-name">{item.subject}</div>

																				<div className="col-lg-3 detail-status">
																					<div className="title red">Submitted</div>

																					<time className="name red">
																						<DisplayDate>{item.created}</DisplayDate>
																					</time>
																				</div>

																				<div className="col-lg-3 detail-status"></div>

																				<div className="col-lg-3 detail-status">
																					<div className="title">Days open</div>

																					<time className="status">{item.daysOpen}</time>
																				</div>
																			</div>
																		</div>

																		<div className="col-auto pdr-0 link-group">
																			<Link className="btn-text complaints-page" to={`/feedback/${item.id}`}>
																				View
																			</Link>
																		</div>
																	</div>
																</div>
															);
														})}

														{maxUserSubmittedFeedback < submittedUserFeedbackData.length && (
															<Button
																className="btn-secondary btn-show-more"
																onClick={() => setMaxUserSubmittedFeedback(submittedUserFeedbackData.length)}
																buttonIcon={<PlusIcon className="link-icon" />}
															>
																Show {submittedUserFeedbackData.length - maxUserSubmittedFeedback} more
															</Button>
														)}
													</section>
												)}

												{inProgressUserFeedbackData && inProgressUserFeedbackData.length > 0 && (
													<section className="info-section complaints-page">
														<div className="section-header bold">In Progress</div>
														{inProgressUserFeedbackData.slice(0, maxUserInProgressFeedback).map((item) => {
															return (
																<div key={item.id} className="row-container">
																	<div className="row detail-row">
																		<div className="col-auto pdl-0 pdr-0">
																			<StatusIcon statusType="warning" />
																		</div>

																		<div className="col-md-auto full-width">
																			<div className="row detail-info">
																				<div className="col-lg-3 detail-name">{item.subject}</div>

																				<div className="col-lg-3 detail-status">
																					<div className="title">Last updated</div>

																					<time className="name">
																						<DisplayDate>{item.updated}</DisplayDate>
																					</time>
																				</div>

																				<div className="col-lg-3 detail-status"></div>

																				<div className="col-lg-3 detail-status">
																					<div className="title">Days open</div>

																					<time className="status">{item.daysOpen}</time>
																				</div>
																			</div>
																		</div>

																		<div className="col-auto pdr-0 link-group">
																			<Link className="btn-text complaints-page" to={`/feedback/${item.id}`}>
																				View
																			</Link>
																		</div>
																	</div>
																</div>
															);
														})}

														{maxUserInProgressFeedback < inProgressUserFeedbackData.length && (
															<Button
																className="btn-secondary btn-show-more"
																onClick={() => setMaxUserInProgressFeedback(inProgressUserFeedbackData.length)}
																buttonIcon={<PlusIcon className="link-icon" />}
															>
																Show {inProgressUserFeedbackData.length - maxUserInProgressFeedback} more
															</Button>
														)}
													</section>
												)}

												{resolvedUserFeedbackData && resolvedUserFeedbackData.length > 0 && (
													<section className="info-section complaints-page">
														<div className="section-header bold">Resolved</div>
														{resolvedUserFeedbackData.slice(0, maxUserResolvedFeedback).map((item) => {
															return (
																<div key={item.id} className="row-container">
																	<div className="row detail-row">
																		<div className="col-auto pdl-0 pdr-0">
																			<StatusIcon statusType="success" />
																		</div>

																		<div className="col-md-auto full-width">
																			<div className="row detail-info">
																				<div className="col-lg-3 detail-name">{item.subject}</div>

																				<div className="col-lg-3 detail-status">
																					{item.status === DomainFeedbackStatus.Resolved ? (
																						<div className="title resolved">Resolved</div>
																					) : null}

																					{item.status === DomainFeedbackStatus.Withdrawn ? (
																						<div className="title withdrawn">Withdrawn</div>
																					) : null}

																					<time className="name">
																						<DisplayDate>{item.resolved}</DisplayDate>
																					</time>
																				</div>

																				<div className="col-lg-3 detail-status"></div>

																				<div className="col-lg-3 detail-status">
																					<div className="title">Days open</div>

																					<time className="status">{item.daysOpen}</time>
																				</div>
																			</div>
																		</div>

																		<div className="col-auto pdr-0 link-group">
																			<Link className="btn-text complaints-page" to={`/feedback/${item.id}`}>
																				View
																			</Link>
																		</div>
																	</div>
																</div>
															);
														})}

														{maxUserResolvedFeedback < resolvedUserFeedbackData.length && (
															<Button
																className="btn-secondary btn-show-more"
																onClick={() => setMaxUserResolvedFeedback(resolvedUserFeedbackData.length)}
																buttonIcon={<PlusIcon className="link-icon" />}
															>
																Show {resolvedUserFeedbackData.length - maxUserResolvedFeedback} more
															</Button>
														)}
													</section>
												)}

												<ScrollToTopButton offsetValue={100} />
											</>
										) : (
											<div className="empty-notification">
												<h2 className="info-text">
													You have not yet submitted any <strong>Feedback</strong>.
												</h2>

												{isOrganisationActive && (
													<Link to="/feedback-submit" className="btn-primary submit-btn">
														Submit feedback
													</Link>
												)}
											</div>
										)}
									</>
								)}
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default Feedback;
